/**
 * The device's load semantics for the running session (`D209`): each
 * exercise's mode — the plan item's, the swap's, else the catalogue copy's
 * (`data/catalogue-snapshot.ts`) — and the latest weigh-in the Query cache
 * holds. Offline it is whatever both last held.
 */
import type { DeviceLoadSemantics } from "@repo/client/session";
import { QueryClientContext } from "@tanstack/react-query";
import { useContext, useMemo } from "react";
import { useCatalogueSnapshot } from "../../../data/catalogue-snapshot";
import { appLocale } from "../../../lib/locale";
import {
  deviceSemanticsOf,
  type LateralityLookup,
  type LoadModeLookup,
  latestCachedWeightKg,
} from "../load-mode";
import type { WorkoutSession } from "../store/session-model";

const NO_SESSION: Pick<WorkoutSession, "items" | "swaps"> = {
  items: [],
  swaps: {},
};

export function useDeviceSemantics(
  session: Pick<WorkoutSession, "items" | "swaps"> | null
): DeviceLoadSemantics {
  const items = useCatalogueSnapshot(
    appLocale().startsWith("pt") ? "pt" : "en"
  );
  // The context, not `useQueryClient`: the after-workout screen also draws
  // in a story with no client, and there the answer is simply "no body".
  const client = useContext(QueryClientContext);
  const lookup: LoadModeLookup = useMemo(() => {
    const modes = new Map(
      (items ?? []).map((item) => [item.id, item.loadMode ?? null])
    );
    return (exerciseId: string) => modes.get(exerciseId) ?? null;
  }, [items]);
  // `D232`: the copy carries a laterality only where it is known.
  const sides: LateralityLookup = useMemo(() => {
    const known = new Map(
      (items ?? []).flatMap((item) =>
        item.laterality ? [[item.id, item.laterality] as const] : []
      )
    );
    return (exerciseId: string) => known.get(exerciseId) ?? null;
  }, [items]);
  const bodyWeightKg =
    client === undefined ? null : latestCachedWeightKg(client);
  const subject = session ?? NO_SESSION;
  return useMemo(
    () => deviceSemanticsOf(subject, bodyWeightKg, lookup, sides),
    [subject, bodyWeightKg, lookup, sides]
  );
}
